import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
  IFilterParams,
} from 'ag-grid-community';

/**
 * The model agSetColumnFilter emits: the selected keys, as strings. `null` in
 * `values` is ag-grid's own "(Blanks)" entry (rows without a value);
 * `notBlank` asks for every row with a value and stands alone (WAP-1753,
 * backend SEN-5445).
 */
export interface SetFilterModel {
  filterType: 'set';
  values: (string | null)[];
  notBlank?: boolean;
}

interface SetFilterEntry {
  key: string;
  label: string;
}

/**
 * Subset of ag-grid's set filter params that columns already declare
 * (nomenclature-column.utils.ts, explicit agSetColumnFilter columns).
 */
interface SetFilterColumnParams {
  values?: unknown[] | ((params: SetValuesParams) => void);
  keyCreator?: (params: { value: unknown }) => string;
  valueFormatter?: (params: { value: unknown }) => string;
  refreshValuesOnOpen?: boolean;
  defaultToNothingSelected?: boolean;
  /** Show the fixed "Is blank" / "Is not blank" entries above the list (columns that can hold no value). */
  blankEntries?: boolean;
}

interface SetValuesParams {
  success: (values: unknown[]) => void;
  colDef: IFilterParams['colDef'];
  api: IFilterParams['api'];
  context: unknown;
}

const FLOATING_LABEL_LIMIT = 3;

/**
 * The set-filter popup for nomenclature and other list columns, in the app's
 * own controls (WAP-1648): a search box, Select all / none, a checkbox per
 * entry, Reset / Apply. Reads the column's existing ag-grid set-filter params
 * (`values`, `keyCreator`, `valueFormatter`, `refreshValuesOnOpen`) so the
 * column helpers stay the single place that knows the values, and emits the
 * standard `{ filterType: 'set', values }` model with string keys, which the
 * backend, saved presets and data-view drill-downs already speak. Selecting
 * every entry, or none, clears the filter like ag-grid's own set filter.
 * The column normaliser (filter-buttons.utils.ts) installs it grid-wide.
 */
@Component({
  selector: 'app-ag-set-filter',
  templateUrl: './ag-set-filter.component.html',
  styleUrls: ['./ag-set-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgSetFilterComponent implements IFilterAngularComp {
  private static nextId = 0;
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private destroyed = false;
  private params!: IFilterParams;
  private columnParams: SetFilterColumnParams = {};
  private hidePopup: (() => void) | null = null;
  /** Applied selection of values; null = none selected. */
  private applied: Set<string> | null = null;
  private appliedBlank = false;
  private appliedNotBlank = false;
  private valuesLoaded = false;

  readonly uid = `ag-set-filter-${AgSetFilterComponent.nextId++}`;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));
  }
  entries: SetFilterEntry[] = [];
  loading = false;
  search = '';
  /** Pending selection shown in the popup; committed on Apply. */
  pending = new Set<string>();
  pendingBlank = false;
  pendingNotBlank = false;

  agInit(params: IFilterParams): void {
    this.params = params;
    this.columnParams = (params.colDef?.filterParams ??
      {}) as SetFilterColumnParams;
  }

  get visibleEntries(): SetFilterEntry[] {
    const needle = this.search.trim().toLowerCase();
    return needle
      ? this.entries.filter((e) => e.label.toLowerCase().includes(needle))
      : this.entries;
  }

  get canApply(): boolean {
    return !this.loading;
  }

  get blankEntries(): boolean {
    return this.columnParams.blankEntries === true;
  }

  isFilterActive(): boolean {
    return this.applied != null || this.appliedBlank || this.appliedNotBlank;
  }

  // * Only client-side grids call this; SSRM sends the model to the backend.
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const value = this.params.getValue(params.node);
    if (this.appliedNotBlank) return value != null;
    if (value == null) return this.appliedBlank;
    const selected = this.applied;
    if (!selected) return !this.appliedBlank;
    return selected.has(this.keyOf(value));
  }

  getModel(): SetFilterModel | null {
    let model: SetFilterModel | null = null;
    if (this.appliedNotBlank) {
      model = { filterType: 'set', values: [], notBlank: true };
    } else if (this.applied || this.appliedBlank) {
      model = {
        filterType: 'set',
        values: [...(this.applied ?? []), ...(this.appliedBlank ? [null] : [])],
      };
    }
    return model;
  }

  setModel(model: SetFilterModel | null): void {
    const values = Array.isArray(model?.values) ? model.values : null;
    this.appliedNotBlank = model?.notBlank === true;
    this.appliedBlank =
      !this.appliedNotBlank && (values?.some((v) => v == null) ?? false);
    this.applied =
      values && !this.appliedNotBlank
        ? new Set(values.filter((v) => v != null).map((v) => String(v)))
        : null;
    if (this.applied?.size === 0) {
      this.applied = null;
    }
    this.syncPendingFromApplied();
  }

  /** Read-only text shown in the floating filter row. */
  getModelAsString(): string {
    let text = '';
    if (this.appliedNotBlank) {
      text = this.translate.instant('ag-set-filter.text.is-not-blank');
    } else if (this.applied || this.appliedBlank) {
      const labels = [...(this.applied ?? [])].map((key) => this.labelOf(key));
      if (this.appliedBlank) {
        labels.unshift(this.translate.instant('ag-set-filter.text.is-blank'));
      }
      text = labels.slice(0, FLOATING_LABEL_LIMIT).join(', ');
      if (labels.length > FLOATING_LABEL_LIMIT) {
        text += ` +${labels.length - FLOATING_LABEL_LIMIT}`;
      }
    }
    return text;
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup ?? null;
    this.search = '';
    // Reopening discards any uncommitted edits from the previous visit.
    this.syncPendingFromApplied();
    if (!this.valuesLoaded || this.columnParams.refreshValuesOnOpen) {
      this.loadValues();
    }
  }

  isSelected(entry: SetFilterEntry): boolean {
    return this.pending.has(entry.key);
  }

  toggleBlank(checked: boolean): void {
    this.pendingBlank = checked;
    if (checked) {
      this.pendingNotBlank = false;
    }
  }

  toggleNotBlank(checked: boolean): void {
    this.pendingNotBlank = checked;
    if (checked) {
      this.pendingBlank = false;
    }
  }

  toggle(entry: SetFilterEntry, checked: boolean): void {
    if (checked) {
      this.pending.add(entry.key);
    } else {
      this.pending.delete(entry.key);
    }
  }

  selectAllVisible(): void {
    for (const entry of this.visibleEntries) {
      this.pending.add(entry.key);
    }
  }

  selectNoneVisible(): void {
    for (const entry of this.visibleEntries) {
      this.pending.delete(entry.key);
    }
  }

  apply(): void {
    if (!this.canApply) return;
    const everything =
      this.entries.length > 0 &&
      this.entries.every((entry) => this.pending.has(entry.key));
    this.appliedNotBlank = this.pendingNotBlank;
    if (this.appliedNotBlank) {
      this.applied = null;
      this.appliedBlank = false;
    } else {
      // Every value plus the blanks is every row: no filter.
      const all = everything && (!this.blankEntries || this.pendingBlank);
      this.applied =
        this.pending.size === 0 || all ? null : new Set(this.pending);
      this.appliedBlank = this.pendingBlank && !all;
    }
    this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  /** Cancel / Escape: drop the uncommitted edits and close; the applied filter stays. */
  cancel(): void {
    this.syncPendingFromApplied();
    this.hidePopup?.();
  }

  reset(): void {
    const wasActive = this.isFilterActive();
    this.applied = null;
    this.appliedBlank = false;
    this.appliedNotBlank = false;
    this.syncPendingFromApplied();
    if (wasActive) {
      this.params.filterChangedCallback();
    }
    this.hidePopup?.();
  }

  private syncPendingFromApplied(): void {
    this.pending = new Set(this.applied ?? []);
    this.pendingBlank = this.appliedBlank;
    this.pendingNotBlank = this.appliedNotBlank;
  }

  private loadValues(): void {
    const source = this.columnParams.values;
    if (typeof source === 'function') {
      this.loading = true;
      source({
        success: (values) => this.setEntries(values),
        colDef: this.params.colDef,
        api: this.params.api,
        context: this.params.context,
      });
    } else {
      this.setEntries(source ?? []);
    }
  }

  private setEntries(values: unknown[]): void {
    // One entry per key: the template tracks by key and a duplicated key is an
    // Angular NG0955 error on every change detection pass (WAP-1675).
    const seen = new Set<string>();
    this.entries = values
      .map((value) => ({ key: this.keyOf(value), label: this.labelFor(value) }))
      .filter((entry) => !seen.has(entry.key) && seen.add(entry.key));
    this.valuesLoaded = true;
    this.loading = false;
    // ag-grid hosts this component in the popup root, outside the app view
    // tree, and runs change detection on it only around its own hooks. A
    // values callback that answers asynchronously (nomenclature HTTP fetch)
    // lands after those, so render the list ourselves (WAP-1674).
    if (!this.destroyed) {
      this.cdr.detectChanges();
    }
  }

  private keyOf(value: unknown): string {
    const keyCreator = this.columnParams.keyCreator;
    return keyCreator ? keyCreator({ value }) : String(value ?? '');
  }

  private labelFor(value: unknown): string {
    const formatter = this.columnParams.valueFormatter;
    const label = formatter ? formatter({ value }) : String(value ?? '');
    return label || this.translate.instant('ag-set-filter.text.empty-label');
  }

  private labelOf(key: string): string {
    return this.entries.find((entry) => entry.key === key)?.label ?? key;
  }
}
